import { useEffect, useRef, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Hash, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils/cn';
import { useIvrEditor } from '../../context';
import { DTMF_KEYS, type DtmfAction, type IvrAudio, type IvrNodeData } from '../../types';
import { NodeCard } from '../NodeCard';

/**
 * DTMF / Gather node. The most complex node in the IVR builder —
 * has a menu prompt, optional welcome / no-input / wrong-input prompts,
 * digit-count + timeout + re-prompt count, a multi-select of accepted
 * digits which DYNAMICALLY creates output handles per accepted digit, and
 * SMS triggers per-digit.
 */
export function DtmfNode(props: NodeProps) {
  const { updateNodeData, onRemove, prompts, smsWebhookFlag, smsWebhookList, readOnly } =
    useIvrEditor();
  const data = props.data as IvrNodeData;

  const action = (data.action ?? {}) as DtmfAction;
  const numDigits = Number(data.numDigits ?? 1);
  // Wait time: 1-15s, defaults to 1 when unset.
  const timeout = data.timeout ?? 1;
  // Re-prompt count: 1-5, stays empty until the user picks a value.
  const rePrompt = data.rePrompt ?? '';

  const WAIT_TIME_MIN = 1;
  const WAIT_TIME_MAX = 15;
  const RE_PROMPT_MIN = 1;
  const RE_PROMPT_MAX = 5;

  const onWaitTimeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (digits === '') {
      updateNodeData(props.id, { timeout: '' });
      return;
    }
    const clamped = Math.min(WAIT_TIME_MAX, Math.max(WAIT_TIME_MIN, Number(digits)));
    updateNodeData(props.id, { timeout: clamped });
  };

  const onWaitTimeBlur = () => {
    if (timeout === '' || Number(timeout) < WAIT_TIME_MIN) {
      updateNodeData(props.id, { timeout: WAIT_TIME_MIN });
    }
  };

  const onRePromptChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (digits === '') {
      updateNodeData(props.id, { rePrompt: '' });
      return;
    }
    const clamped = Math.min(RE_PROMPT_MAX, Math.max(RE_PROMPT_MIN, Number(digits)));
    updateNodeData(props.id, { rePrompt: clamped });
  };

  // Local draft state for "Digits required" — lets the field go empty
  // while typing (so backspace actually clears it) without turning
  // numDigits itself into a string | number union used across the file.
  const [numDigitsInput, setNumDigitsInput] = useState(String(numDigits));

  useEffect(() => {
    setNumDigitsInput(String(numDigits));
  }, [numDigits]);

  const onNumDigitsInputChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setNumDigitsInput(digits);
    if (digits !== '') {
      updateNodeData(props.id, { numDigits: Number(digits) });
    }
  };

  const onNumDigitsBlur = () => {
    if (numDigitsInput === '' || Number(numDigitsInput) < 1) {
      setNumDigitsInput('1');
      updateNodeData(props.id, { numDigits: 1 });
    }
  };

  const onNumericFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  // Inline audio preview for the 3 prompt pickers below (menu / no-input /
  // wrong-input) — lets you listen right inside the sheet.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
    };
  }, []);

  const togglePreview = (url?: string) => {
    if (!url) return;
    if (previewUrl === url) {
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
      setPreviewUrl(null);
      return;
    }
    previewAudioRef.current?.pause();
    const a = new Audio(url);
    a.onended = () => setPreviewUrl(null);
    a.onerror = () => setPreviewUrl(null);
    void a.play();
    previewAudioRef.current = a;
    setPreviewUrl(url);
  };

  const selectedHandles = data.selectedHandles ?? {};
  const smsData = data.smsData ?? {};

  const [open, setOpen] = useState(false);
  // View-Only tile's Play button opens this instead of playing the menu
  // audio directly — shows every setting + every configured audio for
  // this step (wait time, re-prompt count, digits required, SMS on
  // DTMF, and menu / no-input / wrong-input voices) since a superadmin
  // reviewer can't open the edit sheet to read them.
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Closing the modal (X button, overlay click, Escape — anything that
  // flips `open` to false) must stop whatever's playing; otherwise the
  // audio keeps going in the background after the modal is gone.
  const onPreviewModalOpenChange = (next: boolean) => {
    setPreviewModalOpen(next);
    if (!next) {
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
      setPreviewUrl(null);
    }
  };

  const filter = (category: string): IvrAudio[] =>
    prompts
      .filter(
        (p) =>
          p.promptCategory === category &&
          (p.promptStatus === undefined || Number(p.promptStatus) === 1 || p.promptStatus === '1'),
      )
      .map((p) => ({
        id: p.promptId,
        audio: (p as { promptUrl?: string }).promptUrl ?? '',
        duration: (p as { promptDuration?: number }).promptDuration ?? 0,
        name: p.fileName ?? p.promptName,
      }));

  const menuPrompts = filter('menu');
  const noInputPrompts = filter('noinput');
  const wrongInputPrompts = filter('wronginput');

  const setAudio = (key: keyof DtmfAction, value: IvrAudio | null) => {
    updateNodeData(props.id, {
      action: { ...action, [key]: value },
    });
  };

  const onPickMenu = (idStr: string) => {
    const a = menuPrompts.find((p) => String(p.id) === idStr);
    if (a) setAudio('menu', a);
  };
  const onPickNoInput = (idStr: string) => {
    if (!idStr) return setAudio('noInput', null);
    const a = noInputPrompts.find((p) => String(p.id) === idStr);
    if (a) setAudio('noInput', a);
  };
  const onPickWrongInput = (idStr: string) => {
    if (!idStr) return setAudio('wrongInput', null);
    const a = wrongInputPrompts.find((p) => String(p.id) === idStr);
    if (a) setAudio('wrongInput', a);
  };

  /**
   * Toggle whether a DTMF key is an accepted output. Adds/removes the
   * `selectedHandles` entry — the source Handle below renders one per entry.
   */
  const toggleDigit = (digit: string) => {
    const idx = DTMF_KEYS.indexOf(digit as (typeof DTMF_KEYS)[number]);
    if (idx < 0) return;
    const handleId = `${props.id}_handle-${digit}`;
    const next = { ...selectedHandles };
    if (next[handleId]) {
      delete next[handleId];
    } else {
      next[handleId] = {
        type: 'source',
        position: 'bottom',
        left: idx * 10 + 10,
      };
    }
    updateNodeData(props.id, {
      selectedHandles: next,
      numSources: Object.keys(next).map((h) => Number(h.split('-')[1])),
    });
  };

  /** Map handle order to render-left positions, evenly spread along bottom. */
  const acceptedDigits = Object.keys(selectedHandles)
    .map((h) => h.split('-').pop())
    .filter((d): d is string => Boolean(d))
    .sort(
      (a, b) =>
        DTMF_KEYS.indexOf(a as (typeof DTMF_KEYS)[number]) -
        DTMF_KEYS.indexOf(b as (typeof DTMF_KEYS)[number]),
    );

  /**
   * Vertical layout for source handles: stacked along the RIGHT edge of
   * the card, one per accepted digit. Each handle is paired with a small
   * digit badge so users can see which output goes where without opening
   * the sheet. When single-digit mode has no accepted digits yet, render a
   * faded placeholder handle that hints at configuring it.
   */
  const HANDLE_SPACING = 16; // vertical px between stacked handles
  const HANDLE_OFFSET = 32; // px from the top of the card to the first handle
  const handleHeight = Math.max(acceptedDigits.length * HANDLE_SPACING, 0);

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        className="!h-3 !w-3 !border-2 !border-white !bg-violet-500"
      />
      <NodeCard
        verb="dtmf"
        icon={Hash}
        label="DTMF"
        selected={props.selected}
        // View Only (superadmin) page: tile Play opens the full details
        // modal below. Normal edit panel: unchanged — Play button plays
        // the menu audio directly, same as it always has; everything
        // else is read via the Settings sheet like before.
        onPlayClick={readOnly && action.menu?.audio ? () => setPreviewModalOpen(true) : undefined}
        audioUrl={!readOnly ? action.menu?.audio || undefined : undefined}
        configurable={!readOnly}
        removable={!readOnly}
        status={
          action.menu?.audio && (numDigits > 1 || acceptedDigits.length > 0)
            ? 'configured'
            : 'incomplete'
        }
        className={
          numDigits === 1 && acceptedDigits.length > 1
            ? `pr-3` // extra right padding so handles + badges don't overlap content
            : undefined
        }
        // Reserve extra vertical space below the card body when many digits
        // are accepted, so the right-edge handle stack fits inside the
        // node's bounding box.
        summary={
          action.menu?.name ? (
            <span className="truncate">
              <span className="font-mono">{action.menu.name}</span>
              <span className="text-muted-foreground">
                {' '}
                ·{' '}
                {numDigits === 1
                  ? `${acceptedDigits.length} key${acceptedDigits.length === 1 ? '' : 's'}`
                  : `${numDigits} digits`}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">Pick menu + DTMF keys</span>
          )
        }
        onConfigure={() => setOpen(true)}
        onRemove={() => onRemove(props.id)}
      >
        {/*
         * Spacer so the node's bounding box grows when we have many digits,
         * keeping the stacked source handles inside the card outline.
         */}
        {numDigits === 1 && acceptedDigits.length > 0 && (
          <div
            aria-hidden
            style={{ height: `${handleHeight}px` }}
            className="mt-1 flex flex-col justify-start"
          >
            {acceptedDigits.map((digit) => (
              <div
                key={digit}
                className="flex items-center justify-end pr-2 text-[8.5px] font-semibold leading-none text-violet-700"
                style={{
                  height: `${HANDLE_SPACING}px`,
                }}
              >
                <span className="rounded bg-violet-100 px-1 py-0.5 font-mono">{digit}</span>
              </div>
            ))}
          </div>
        )}
      </NodeCard>

      {/*
       * Per-digit source handles — stacked vertically on the right edge so
       * each accepted DTMF gets a clearly-labeled, individually-targetable
       * connection point.
       */}
      {numDigits === 1 ? (
        acceptedDigits.length === 0 ? (
          // Placeholder: invisible disabled handle so users see no output
          // until they pick a digit. Hint shown above.
          <Handle
            id={`${props.id}_placeholder`}
            type="source"
            position={Position.Right}
            isConnectable={false}
            className="!h-3 !w-3 !border-2 !border-dashed !border-violet-300 !bg-card"
            title="Pick DTMF keys in the config to enable connections"
          />
        ) : (
          acceptedDigits.map((digit, i) => {
            const handleId = `${props.id}_handle-${digit}`;
            const top = HANDLE_OFFSET + i * HANDLE_SPACING;
            return (
              <Handle
                key={handleId}
                id={handleId}
                type="source"
                position={Position.Right}
                style={{ top: `${top}px` }}
                className="!h-3 !w-3 !border-2 !border-white !bg-violet-500"
                data-digit={digit}
              />
            );
          })
        )
      ) : (
        <Handle
          id={`${props.id}_single_source`}
          type="source"
          position={Position.Right}
          className="!h-3 !w-3 !border-2 !border-white !bg-violet-500"
        />
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>DTMF / Gather</SheetTitle>
            <SheetDescription>
              Play the menu, capture caller input, and branch on the pressed key.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>
                Menu audio
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Select value={String(action.menu?.id ?? '')} onValueChange={onPickMenu}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a menu prompt" />
                  </SelectTrigger>
                  <SelectContent>
                    {menuPrompts.map((p) => (
                      <SelectItem key={String(p.id)} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {action.menu?.audio && (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9 shrink-0"
                    onClick={() => togglePreview(action.menu?.audio)}
                    aria-label={previewUrl === action.menu?.audio ? 'Pause menu audio' : 'Play menu audio'}
                  >
                    {previewUrl === action.menu?.audio ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>
                  Wait time (s) <span className="text-muted-foreground">(range: 1-15s)</span>
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <Input
                  inputMode="numeric"
                  type="number"
                  min={WAIT_TIME_MIN}
                  max={WAIT_TIME_MAX}
                  value={String(timeout)}
                  onChange={(e) => onWaitTimeChange(e.target.value)}
                  onBlur={onWaitTimeBlur}
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Re-prompt count <span className="text-muted-foreground">(range: 1-5)</span>
                </Label>
                <Input
                  inputMode="numeric"
                  type="number"
                  min={RE_PROMPT_MIN}
                  max={RE_PROMPT_MAX}
                  value={String(rePrompt)}
                  onChange={(e) => onRePromptChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Digits required</Label>
                <Input
                  inputMode="numeric"
                  type="number"
                  min={1}
                  value={numDigitsInput}
                  onChange={(e) => onNumDigitsInputChange(e.target.value)}
                  onFocus={onNumericFocus}
                  onBlur={onNumDigitsBlur}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>No-input audio (optional)</Label>
              <div className="flex items-center gap-2">
                <Select value={String(action.noInput?.id ?? '')} onValueChange={onPickNoInput}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick (or leave blank)" />
                  </SelectTrigger>
                  <SelectContent>
                    {noInputPrompts.map((p) => (
                      <SelectItem key={String(p.id)} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {action.noInput?.audio && (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9 shrink-0"
                    onClick={() => togglePreview(action.noInput?.audio)}
                    aria-label={
                      previewUrl === action.noInput?.audio ? 'Pause no-input audio' : 'Play no-input audio'
                    }
                  >
                    {previewUrl === action.noInput?.audio ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Wrong-input audio (optional)</Label>
              <div className="flex items-center gap-2">
                <Select value={String(action.wrongInput?.id ?? '')} onValueChange={onPickWrongInput}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick (or leave blank)" />
                  </SelectTrigger>
                  <SelectContent>
                    {wrongInputPrompts.map((p) => (
                      <SelectItem key={String(p.id)} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {action.wrongInput?.audio && (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9 shrink-0"
                    onClick={() => togglePreview(action.wrongInput?.audio)}
                    aria-label={
                      previewUrl === action.wrongInput?.audio
                        ? 'Pause wrong-input audio'
                        : 'Play wrong-input audio'
                    }
                  >
                    {previewUrl === action.wrongInput?.audio ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Accepted digits — only when single-digit gather. */}
            {numDigits === 1 && (
              <div className="space-y-2">
                <Label>Accepted DTMF keys</Label>
                <div className="grid grid-cols-6 gap-1.5">
                  {DTMF_KEYS.map((d) => {
                    const active = !!selectedHandles[`${props.id}_handle-${d}`];
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDigit(d)}
                        className={cn(
                          'flex h-8 items-center justify-center rounded border font-mono text-xs transition-colors',
                          active
                            ? 'border-violet-500 bg-violet-100 text-violet-900'
                            : 'border-border bg-card text-muted-foreground hover:border-violet-300',
                        )}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Each accepted key gets its own output connection point on the node.
                </p>
              </div>
            )}

            {/* Per-digit SMS bindings */}
            {smsWebhookFlag && acceptedDigits.length > 0 && (
              <div className="space-y-2">
                <Label>SMS on DTMF (optional)</Label>
                <div className="space-y-2 rounded-md border border-border p-2">
                  {acceptedDigits.map((d) => {
                    const key = `DTMF_${d}`;
                    const enabled = !!smsData[key];
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 rounded border border-border bg-muted/20 p-2"
                      >
                        <span className="w-6 font-mono text-xs">{d}</span>
                        <div className="flex-1">
                          {enabled ? (
                            <Select
                              value={String(smsData[key]?.id ?? '')}
                              onValueChange={(v) => {
                                const hook = smsWebhookList.find((w) => String(w.id) === v);
                                if (!hook) return;
                                updateNodeData(props.id, {
                                  smsData: {
                                    ...smsData,
                                    [key]: {
                                      payload: String(hook.id),
                                      id: hook.id,
                                    },
                                  },
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Pick an SMS webhook" />
                              </SelectTrigger>
                              <SelectContent>
                                {smsWebhookList.map((w) => (
                                  <SelectItem key={w.id} value={String(w.id)}>
                                    {w.webhookName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs text-muted-foreground">Not sending</span>
                          )}
                        </div>
                        <Switch
                          checked={enabled}
                          onCheckedChange={(v) => {
                            const next = { ...smsData };
                            if (v) next[key] = {};
                            else delete next[key];
                            updateNodeData(props.id, { smsData: next });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <SheetFooter className="mt-6">
            <Button onClick={() => setOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/*
       * View-Only "details" modal — opened from the node card's Play
       * button (only in readOnly mode, see NodeCard's onPlayClick above).
       * Shows every setting AND every configured audio for this step, so
       * a superadmin reviewer can see the full config without an edit
       * sheet.
       */}
      <Dialog open={previewModalOpen} onOpenChange={onPreviewModalOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>DTMF Step Configuration</DialogTitle>
            <DialogDescription>
                Review every setting and voice prompt configured for this step.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Wait time / Re-prompt count / Digits required */}
            <div className="grid grid-cols-3 gap-2 rounded-md border border-border p-2.5">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Wait time
                </div>
                <div className="text-sm font-medium">{timeout !== '' ? `${timeout}s` : '—'}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Re-prompt
                </div>
                <div className="text-sm font-medium">{rePrompt !== '' ? rePrompt : '—'}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Digits required
                </div>
                <div className="text-sm font-medium">{numDigits}</div>
              </div>
            </div>

            {/* Accepted DTMF keys — only relevant in single-digit mode. */}
            {numDigits === 1 && (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Accepted DTMF keys
                </div>
                {acceptedDigits.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {acceptedDigits.map((d) => (
                      <span
                        key={d}
                        className="rounded bg-violet-100 px-2 py-0.5 font-mono text-xs text-violet-900"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">None selected.</p>
                )}
              </div>
            )}

            {/* SMS on DTMF — which webhook (if any) fires per accepted digit. */}
            {smsWebhookFlag && acceptedDigits.length > 0 && (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  SMS on DTMF
                </div>
                <div className="space-y-1.5">
                  {acceptedDigits.map((d) => {
                    const key = `DTMF_${d}`;
                    const entry = smsData[key];
                    const hookName = entry
                      ? smsWebhookList.find((w) => String(w.id) === String(entry.id))?.webhookName
                      : null;
                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between rounded border border-border bg-muted/20 px-2.5 py-1.5 text-xs"
                      >
                        <span className="font-mono">{d}</span>
                        <span className={hookName ? 'text-foreground' : 'text-muted-foreground'}>
                          {hookName ?? 'Not sending'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Menu / no-input / wrong-input voices — each with its own
             * play/pause control. */}
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Voices
              </div>
              <div className="space-y-2">
                {action.menu?.audio && (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border p-2.5">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Menu voice
                      </div>
                      <div className="truncate font-mono text-sm">{action.menu.name}</div>
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 shrink-0"
                      onClick={() => togglePreview(action.menu?.audio)}
                      aria-label={previewUrl === action.menu?.audio ? 'Pause menu voice' : 'Play menu voice'}
                    >
                      {previewUrl === action.menu?.audio ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
                {action.noInput?.audio && (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border p-2.5">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        No-input voice
                      </div>
                      <div className="truncate font-mono text-sm">{action.noInput.name}</div>
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 shrink-0"
                      onClick={() => togglePreview(action.noInput?.audio)}
                      aria-label={
                        previewUrl === action.noInput?.audio ? 'Pause no-input voice' : 'Play no-input voice'
                      }
                    >
                      {previewUrl === action.noInput?.audio ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
                {action.wrongInput?.audio && (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border p-2.5">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Wrong-input voice
                      </div>
                      <div className="truncate font-mono text-sm">{action.wrongInput.name}</div>
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-9 w-9 shrink-0"
                      onClick={() => togglePreview(action.wrongInput?.audio)}
                      aria-label={
                        previewUrl === action.wrongInput?.audio
                          ? 'Pause wrong-input voice'
                          : 'Play wrong-input voice'
                      }
                    >
                      {previewUrl === action.wrongInput?.audio ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
                {!action.noInput?.audio && !action.wrongInput?.audio && (
                  <p className="text-xs text-muted-foreground">
                    No-input / wrong-input voices aren&apos;t set on this step.
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}